import React, { useState, useEffect } from 'react';
import { TextField, Grid, Typography, Button, Select, MenuItem, Divider, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, CircularProgress, Tooltip as MuiTooltip, Collapse, Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import customItemData from '../../data/customItemData.json';
import ItemDetails from "../templates/shared/ItemDetails";
import calculateUtility from '../../utility/calculateUtility';
import { db, auth } from '../../firebase/config';
import { collection, addDoc, setDoc, doc, onSnapshot, serverTimestamp, query, deleteDoc, where } from 'firebase/firestore';

const CustomItem = ({ template, selectedSlot, selectedItem, addGearToSlot, gearBySlot }) => {
    const [itemName, setItemName] = useState('');
    const [bonuses, setBonuses] = useState([]);
    const [itemNameError, setItemNameError] = useState(false);
    const [savedItems, setSavedItems] = useState([]);
    const [loadingSaved, setLoadingSaved] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [editingDocId, setEditingDocId] = useState(null);
    const [openSaved, setOpenSaved] = useState({});

    // Normalize UI slot names for storage and querying
    const normalizeSlotForStorage = (slot) => {
        if (slot === 'L Ring' || slot === 'R Ring') return 'Ring';
        if (slot === 'L Wrist' || slot === 'R Wrist') return 'Bracer';
        return slot;
    };

    // For querying, include normalized and legacy variants so older saves continue to appear
    const getStorageSlotQueryValues = (slot) => {
        if (slot === 'L Ring' || slot === 'R Ring') return ['Ring', 'L Ring', 'R Ring'];
        if (slot === 'L Wrist' || slot === 'R Wrist') return ['Bracer', 'L Wrist', 'R Wrist'];
        return [slot];
    };

    const toggleOpenSaved = (id) => {
        setOpenSaved((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    useEffect(() => {
        if (gearBySlot[selectedItem] && gearBySlot[selectedItem].tags && gearBySlot[selectedItem].tags.includes('custom')) {
            // Populate with existing custom item data
            setItemName(gearBySlot[selectedItem].name || '');
            setBonuses(gearBySlot[selectedItem].bonuses || []);
        } else {
            // Reset for new custom item
            setItemName('');
            setBonuses([]);
        }
    }, [gearBySlot, selectedItem]);

    // Subscribe to user's saved custom items
    useEffect(() => {
        const uid = auth?.currentUser?.uid;
        if (!uid) {
            setSavedItems([]);
            return;
        }
        setLoadingSaved(true);
        const slotsForQuery = getStorageSlotQueryValues(selectedItem);
        const q = query(
            collection(db, 'users', uid, 'customItems'),
            where('realm', '==', template.realm),
            where('slot', 'in', slotsForQuery)
        );
        const unsub = onSnapshot(q, (snap) => {
            const items = [];
            snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
            // Sort client-side by updatedAt desc to avoid extra index requirements
            items.sort((a, b) => {
                const aTime = a.updatedAt?.seconds || 0;
                const bTime = b.updatedAt?.seconds || 0;
                return bTime - aTime;
            });
            setSavedItems(items);
            setLoadingSaved(false);
        }, (err) => {
            console.error('Error loading custom items', err);
            setLoadingSaved(false);
        });
        return () => unsub();
    }, [template.realm, selectedItem]);

    const getBonusTypes = () => Object.keys(customItemData[template.realm] || {});
    const getBonusIds = (type) => customItemData[template.realm][type] || [];

    const addBonus = () => {
        const bonusTypes = getBonusTypes();
        const initialType = bonusTypes[0];
        const initialIds = getBonusIds(initialType);
        setBonuses([...bonuses, { type: initialType, id: initialIds.length ? initialIds[0] : undefined, value: '' }]);
    };

    const handleBonusChange = (index, field) => (event) => {
        const newBonuses = [...bonuses];
        let newValue = event.target.value;

        // Convert the value to an integer if the field being changed is 'value'
        if (field === 'value') {
            newValue = newValue === '' ? '' : parseInt(newValue, 10) || 0;
        }

        newBonuses[index][field] = newValue;

        // Initialize or remove 'id' if bonus type changes
        if (field === 'type') {
            const newIds = getBonusIds(event.target.value);
            newBonuses[index].id = newIds.length ? newIds[0] : undefined;
        }
        setBonuses(newBonuses);
    };

    const removeBonus = (index) => setBonuses(bonuses.filter((_, i) => i !== index));

    const generateUniqueId = () => {
        return Date.now().toString(36) + Math.random().toString(36).slice(2);
    };

    const buildCustomGear = () => {
        // Filter out bonuses with empty or invalid values
        const validBonuses = bonuses
            .filter(b => b.value !== '' && b.value != null)
            .map(b => (b.id === undefined || b.id === '' ? { type: b.type, value: b.value } : b));
        const gear = {
            id: generateUniqueId(),
            name: itemName,
            slot: selectedItem,
            realm: template.realm,
            bonuses: validBonuses,
            tags: ["custom"],
            owner: auth?.currentUser?.uid || null
        };
        // Attach computed utility for immediate use in UI and downstream logic
        return { ...gear, utility: calculateUtility(gear) };
    };

    const handleAddCustomGearToSlot = (activateItem = true) => {
        if (!selectedSlot) {
            console.log("Please select a slot.");
            return;
        }

        if (!itemName) {
            // Set error state if itemName is empty
            setItemNameError(true);
            console.log("Item name is required.");
            return;
        }

        if (selectedSlot && itemName) {
            const customGear = buildCustomGear();
            addGearToSlot(selectedItem, customGear, activateItem);
            setBonuses([]);
            setItemName('');
            setEditingDocId(null);
        } else {
            console.log("Please select a slot and enter an item name.");
        }
    };

    const saveCustomItem = async (asNew = false) => {
        setSaveError('');
        const uid = auth?.currentUser?.uid;
        if (!uid) {
            setSaveError('You must be signed in to save custom items.');
            return;
        }
        if (!itemName) {
            setItemNameError(true);
            setSaveError('Item name is required.');
            return;
        }
        const validBonuses = bonuses
            .filter(b => b.value !== '' && b.value != null)
            .map(b => (b.id === undefined || b.id === '' ? { type: b.type, value: b.value } : b));
        const data = {
            name: itemName,
            slot: normalizeSlotForStorage(selectedItem),
            realm: template.realm,
            bonuses: validBonuses,
            tags: ["custom"],
            owner: uid,
            updatedAt: serverTimestamp(),
        };
        try {
            if (!asNew && editingDocId) {
                await setDoc(doc(db, 'users', uid, 'customItems', editingDocId), data, { merge: true });
            } else {
                await addDoc(collection(db, 'users', uid, 'customItems'), {
                    ...data,
                    createdAt: serverTimestamp()
                });
                // After saving as new, set editing id to null to avoid accidental overwrites
                setEditingDocId(null);
            }
        } catch (e) {
            console.error('Failed to save custom item', e);
            setSaveError('Failed to save custom item.');
        }
    };

    const deleteSavedItem = async (docId) => {
        const uid = auth?.currentUser?.uid;
        if (!uid) return;
        try {
            await deleteDoc(doc(db, 'users', uid, 'customItems', docId));
            if (editingDocId === docId) setEditingDocId(null);
        } catch (e) {
            console.error('Failed to delete custom item', e);
        }
    };

    const loadSavedToForm = (saved) => {
        setItemName(saved.name || '');
        setBonuses(saved.bonuses || []);
        setEditingDocId(saved.id);
    };

    const addSavedToSlot = (saved, activateItem = true) => {
        if (!selectedSlot) {
            console.log("Please select a slot.");
            return;
        }
        const gear = {
            id: generateUniqueId(),
            name: saved.name,
            slot: selectedItem,
            realm: saved.realm,
            bonuses: saved.bonuses || [],
            tags: ["custom"],
            owner: saved.owner || template?.owner || null
        };
        const gearWithUtility = { ...gear, utility: calculateUtility(gear) };
        addGearToSlot(selectedItem, gearWithUtility, activateItem);
    };

    const clearEdit = () => {
        setItemName('');
        setBonuses([]);
        setItemNameError(false);
        setEditingDocId(null);
        setSaveError('');
    };

    return (
        <div>
            {/*<Typography variant="h5">Custom Item</Typography>*/}
            {!selectedSlot && <Typography color="error">Please select an item slot first.</Typography>}
            {selectedSlot && (
                <>
                    <TextField
                        label="Item Name"
                        value={itemName}
                        onChange={(e) => {
                            setItemName(e.target.value);
                            setItemNameError(false); // Reset error state when user starts typing
                        }}
                        fullWidth
                        margin="normal"
                        error={itemNameError} // Use error state to control the error display
                        helperText={itemNameError ? "Item name is required" : ""} // Display helper text when there's an error
                    />
                    {bonuses.map((bonus, index) => (
                        <Grid container spacing={2} key={index} alignItems="center" mb={2}>
                            <Grid item>
                                <Select value={bonus.type} onChange={handleBonusChange(index, 'type')} sx={{ minWidth: 150 }}>
                                    {getBonusTypes().map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                                </Select>
                            </Grid>
                            {bonus.id !== undefined && (
                                <Grid item>
                                    <Select value={bonus.id} onChange={handleBonusChange(index, 'id')} sx={{ minWidth: 150 }}>
                                        {getBonusIds(bonus.type).map(id => <MenuItem key={id} value={id}>{id}</MenuItem>)}
                                    </Select>
                                </Grid>
                            )}
                            <Grid item>
                                <TextField
                                    label="Value"
                                    type="number"
                                    value={bonus.value} // Ensure this is linked to your bonus.value
                                    onChange={handleBonusChange(index, 'value')}
                                />
                            </Grid>
                            <Grid item>
                                <Button onClick={() => removeBonus(index)}>Remove</Button>
                            </Grid>
                        </Grid>
                    ))}
                    {/* Row 1: Add Bonus only */}
                    <div style={{ margin: '20px 0 8px' }}>
                        <Button onClick={addBonus} variant="outlined">Add Bonus</Button>
                    </div>

                    {/* Row 2: Other actions with consistent spacing */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                        <Button variant="contained" color="primary" onClick={() => handleAddCustomGearToSlot(true)}>Add Custom Item to {selectedSlot}</Button>
                        <Button variant="outlined" color="secondary" onClick={() => handleAddCustomGearToSlot(false)}>Add to Inventory</Button>
                        <MuiTooltip title={template?.owner ? (editingDocId ? 'Update this saved item' : 'Save as a new custom item') : 'Sign in to save'}>
                            <span>
                                <Button startIcon={<SaveIcon />} variant="outlined" onClick={() => saveCustomItem(false)} disabled={!template?.owner}>
                                    {editingDocId ? 'Save Update' : 'Save'}
                                </Button>
                            </span>
                        </MuiTooltip>
                        {editingDocId && (
                            <Button startIcon={<AddIcon />} variant="text" onClick={() => saveCustomItem(true)}>
                                Save As New
                            </Button>
                        )}
                        {editingDocId && (
                            <Button variant="text" color="warning" onClick={clearEdit}>Clear Edit</Button>
                        )}
                    </Box>
                    {saveError && <Typography color="error" variant="body2">{saveError}</Typography>}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6">Saved Custom Items</Typography>
                    {!template?.owner && (
                        <Typography variant="body2" color="textSecondary">Sign in to view and save your custom items.</Typography>
                    )}
                    {template?.owner && (
                        loadingSaved ? (
                            <CircularProgress size={24} />
                        ) : (
                            <List dense>
                                {savedItems.length === 0 && (
                                    <Typography variant="body2" color="textSecondary">No custom items saved yet.</Typography>
                                )}
                                {savedItems.map((si) => {
                                    const open = !!openSaved[si.id];
                                    return (
                                        <React.Fragment key={si.id}>
                                            <ListItem divider>
                                                <IconButton edge="start" size="small" onClick={() => toggleOpenSaved(si.id)} sx={{ mr: 1 }}>
                                                    {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                                <ListItemText
                                                    primary={si.name}
                                                    secondary={`Slot: ${si.slot || selectedItem}`}
                                                />
                                                <ListItemSecondaryAction>
                                                    <MuiTooltip title="Load into form for editing">
                                                        <IconButton edge="end" onClick={() => loadSavedToForm(si)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </MuiTooltip>
                                                    <MuiTooltip title={`Use in ${selectedSlot || 'slot'}`}>
                                                        <span>
                                                            <IconButton edge="end" onClick={() => addSavedToSlot(si, true)} disabled={!selectedSlot}>
                                                                <AddIcon />
                                                            </IconButton>
                                                        </span>
                                                    </MuiTooltip>
                                                    <MuiTooltip title="Delete">
                                                        <IconButton edge="end" onClick={() => deleteSavedItem(si.id)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </MuiTooltip>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            <Collapse in={open} timeout="auto" unmountOnExit>
                                                <Box sx={{ pl: 6, pr: 2, pb: 1 }}>
                                                    <ItemDetails item={{ ...si, id: si.id, utility: calculateUtility(si) }} />
                                                </Box>
                                            </Collapse>
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        )
                    )}
                    {/*<Typography variant="h6">Custom Item Preview:</Typography>*/}
                    {/*<pre>{JSON.stringify({ name: itemName, slot: selectedSlot, realm: template.realm, bonuses, tags: ["custom"] }, null, 2)}</pre>*/}
                </>
            )}
        </div>
    );
};

export default CustomItem;
